import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';

function Contact() {
  const [content, setContent] = useState({
    title: 'Contact Us',
    schoolName: '',
    principalName: '',
    principalEmail: '',
    address: '',
    phone: '',
    alternatePhone: '',
    email: '',
    alternateEmail: '',
    googleMapEmbedUrl: '',
    whatsappNumber: '',
    officeHours: 'Monday to Friday: 8:00 AM - 3:00 PM\nSaturday: 8:00 AM - 12:00 PM\nSunday: Closed',
    sliderImages: []
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getPageContent('contact');
        setContent(prev => ({ ...prev, ...data }));
      } catch (error) { 
        console.error(error); 
      } finally { 
        setLoading(false); 
      }
    };
    loadData();
    const unsubscribe = subscribeToPage('contact', (data) => { 
      if (data) setContent(prev => ({ ...prev, ...data })); 
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const whatsappNumber = content.whatsappNumber || content.phone?.replace(/\D/g, '') || '';
    
    if (!whatsappNumber) {
      alert('WhatsApp number not configured. Please contact the school directly.');
      return;
    }
    
    // Clean phone number (remove non-digits)
    const cleanNumber = whatsappNumber.replace(/\D/g, '');
    
    // Create message
    const message = `*New Enquiry from School Website*\n\n` +
      `*Name:* ${formData.name}\n` +
      `*Email:* ${formData.email}\n` +
      `*Subject:* ${formData.subject}\n` +
      `*Message:*\n${formData.message}\n\n` +
      `*Sent from:* ${window.location.href}`;
    
    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
    
    setSending(true);
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Reset form after a short delay
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setSending(false);
      alert('Thank you for your message! WhatsApp will open to complete your enquiry.');
    }, 1000);
  };

  const getMapSrc = (embedCode) => {
    if (!embedCode) return '';
    const srcMatch = embedCode.match(/src="([^"]+)"/);
    return srcMatch ? srcMatch[1] : embedCode;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Contact Page...</p>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {/* Hero Section - No banner */}
    
        <div className="container">
          <h1>{content.title}</h1>
          <div className="hero-divider"></div>
          <p>Get in touch with us • We're here to help</p>
        </div>

      <div className="container">
        <div className="contact-wrapper">
          {/* Contact Information Section */}
          <div className="contact-info">
            <h2>Get in Touch</h2>
            <div className="info-list">
              {content.schoolName && (
                <div className="info-item">
                  <div className="info-icon">🏫</div>
                  <div>
                    <h4>School</h4>
                    <p>{content.schoolName}</p>
                  </div>
                </div>
              )}
              {content.principalName && (
                <div className="info-item">
                  <div className="info-icon">👨‍🏫</div>
                  <div>
                    <h4>Principal</h4>
                    <p>{content.principalName}</p>
                    {content.principalEmail && <p>{content.principalEmail}</p>}
                  </div>
                </div>
              )}
              {content.address && (
                <div className="info-item">
                  <div className="info-icon">📍</div>
                  <div>
                    <h4>Address</h4>
                    <p>{content.address}</p>
                  </div>
                </div>
              )}
              {(content.phone || content.alternatePhone) && (
                <div className="info-item">
                  <div className="info-icon">📞</div>
                  <div>
                    <h4>Phone</h4>
                    {content.phone && <p>{content.phone}</p>}
                    {content.alternatePhone && <p>{content.alternatePhone}</p>}
                  </div>
                </div>
              )}
              {(content.email || content.alternateEmail) && (
                <div className="info-item">
                  <div className="info-icon">✉️</div>
                  <div>
                    <h4>Email</h4>
                    {content.email && <p>{content.email}</p>}
                    {content.alternateEmail && <p>{content.alternateEmail}</p>}
                  </div>
                </div>
              )}
              {content.whatsappNumber && (
                <div className="info-item">
                  <div className="info-icon">💬</div>
                  <div>
                    <h4>WhatsApp</h4>
                    <p>{content.whatsappNumber}</p>
                  </div>
                </div>
              )}
              {content.officeHours && (
                <div className="info-item">
                  <div className="info-icon">⏰</div>
                  <div>
                    <h4>Office Hours</h4>
                    <p style={{whiteSpace:'pre-line'}}>{content.officeHours}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Contact Form Section - WhatsApp Integration */}
          <div className="contact-form">
            <h2>Send Message</h2>
            <form onSubmit={handleSubmit}>
              <input 
                type="text" 
                name="name"
                placeholder="Your Name" 
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <input 
                type="email" 
                name="email"
                placeholder="Your Email" 
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <input 
                type="text" 
                name="subject"
                placeholder="Subject" 
                value={formData.subject}
                onChange={handleInputChange}
                required
              />
              <textarea 
                name="message"
                rows="5" 
                placeholder="Your Message" 
                value={formData.message}
                onChange={handleInputChange}
                required
              ></textarea>
              <button type="submit" disabled={sending}>
                {sending ? 'Sending...' : 'Send via WhatsApp 💬'}
              </button>
            </form>
            <p className="form-note">
              Your message will be sent via WhatsApp to the school administration.
              {!content.whatsappNumber && content.phone && (
                <span className="warning"> Note: WhatsApp number not configured. Using phone number: {content.phone}</span>
              )}
            </p>
          </div>
        </div>

        {/* Google Map */}
        {content.googleMapEmbedUrl && (
          <div className="map-container">
            <iframe 
              src={getMapSrc(content.googleMapEmbedUrl)} 
              title="School Location" 
              width="100%" 
              height="350" 
              style={{border:0}} 
              allowFullScreen 
              loading="lazy" 
            />
          </div>
        )}

        {/* Back to Academics Link */}
        <div className="back-link">
          <Link to="/academics" className="back-btn">
            ← Back to Academics
          </Link>
        </div>
      </div>

      <style>{`
        .contact-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: #1a3a6b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 24px 60px;
        }

        /* Page Hero */
        .page-hero {
          background: linear-gradient(135deg, #1a3a6b, #f59e0b);
          color: white;
          text-align: center;
          padding: 60px 20px;
          margin-bottom: 40px;
        }

        .page-hero h1 {
          font-size: 48px;
          margin-bottom: 20px;
          font-weight: 700;
        }

        .hero-divider {
          width: 60px;
          height: 4px;
          background: white;
          margin: 0 auto 20px;
          border-radius: 2px;
        }

        .page-hero p {
          font-size: 18px;
          opacity: 0.9;
        }

        /* Contact Wrapper */
        .contact-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 50px;
          margin-bottom: 50px;
        }

        /* Contact Info Section */
        .contact-info h2, .contact-form h2 {
          font-size: 28px;
          color: #1a3a6b;
          margin-bottom: 25px;
          font-weight: 700;
        }

        .info-list {
          display: flex;
          flex-direction: column;
          gap: 25px;
        }

        .info-item {
          display: flex;
          gap: 18px;
          align-items: flex-start;
        }

        .info-icon {
          width: 55px;
          height: 55px;
          background: #1a3a6b;
          color: white;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          flex-shrink: 0;
          transition: transform 0.3s;
        }

        .info-item:hover .info-icon {
          transform: scale(1.05);
          background: #f59e0b;
        }

        .info-item h4 {
          color: #1a3a6b;
          margin-bottom: 6px;
          font-size: 16px;
          font-weight: 700;
        }

        .info-item p {
          color: #64748b;
          line-height: 1.6;
          margin: 4px 0;
        }

        /* Contact Form Section */
        .contact-form form {
          background: white;
          padding: 35px;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .contact-form input, 
        .contact-form textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          margin-bottom: 16px;
          font-family: inherit;
          font-size: 14px;
          transition: all 0.3s;
        }

        .contact-form input:focus, 
        .contact-form textarea:focus {
          outline: none;
          border-color: #1a3a6b;
        }

        .contact-form button {
          background: #1a3a6b;
          color: white;
          padding: 14px 30px;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          font-weight: 700;
          width: 100%;
          font-size: 16px;
          transition: all 0.3s;
        }

        .contact-form button:hover {
          background: #f59e0b;
          transform: translateY(-2px);
        }

        .contact-form button:disabled {
          background: #94a3b8;
          cursor: not-allowed;
          transform: none;
        }

        .form-note {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 12px;
          text-align: center;
        }

        .warning {
          color: #f59e0b;
          font-weight: 500;
        }

        /* Map Container */
        .map-container {
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          margin-bottom: 40px;
        }

        /* Back Link */
        .back-link {
          text-align: center;
          margin-top: 20px;
        }

        .back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #e2e8f0;
          color: #475569;
          padding: 12px 28px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.3s;
        }

        .back-btn:hover {
          background: #1a3a6b;
          color: white;
          transform: translateX(-5px);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 0 20px 50px;
          }
        }

        @media (max-width: 768px) {
          .page-hero {
            padding: 50px 20px;
          }
          
          .page-hero h1 {
            font-size: 32px;
          }
          
          .page-hero p {
            font-size: 16px;
          }

          .contact-wrapper {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .contact-info h2, .contact-form h2 {
            font-size: 24px;
          }

          .contact-form form {
            padding: 25px;
          }

          .info-item {
            gap: 12px;
          }

          .info-icon {
            width: 45px;
            height: 45px;
            font-size: 22px;
          }
        }

        @media (max-width: 480px) {
          .page-hero h1 {
            font-size: 28px;
          }

          .contact-form form {
            padding: 20px;
          }

          .info-item {
            flex-direction: column;
            text-align: center;
          }

          .info-icon {
            margin: 0 auto;
          }
        }
      `}</style>
    </div>
  );
}

export default Contact;